import React, { ReactNode } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import KenColors from './kenColors';

interface GlobalThemeProviderProps {
  children: ReactNode;
}

const GlobalThemeProvider: React.FC<GlobalThemeProviderProps> = ({ children }) => {
  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
        HD: 1500,
        xxl: 1440, // Extra wide desktops (e.g., 1440px width monitors)
        fullHD: 1710, // Full HD (1920px width) or larger screens like wide desktop monitors
        ultraHD: 1920, // 4k or larger monitors
      },
    },
  });

  const responsive = createTheme({
    ...theme,
    palette: {
      KenColors: { ...KenColors },
      primary: {
        light: '#F0EDF6',
        main: '#604797',
        dark: '#604797',
        contrastText: '#fff',
      },
      secondary: {
        light: '#ff7961',
        main: '#f44336',
        dark: '#ba000d',
        contrastText: '#000',
      },
      error: {
        main: '#E75547',
      },
      success: {
        main: '#1BBE75',
      },
      card: {
        backgroundColor: '#F7F8FB',
        hover: '#EDF2FF',
      },
      labels: {
        main: '#061938',
        light: '#7A869A',
        medium: '#505F79',
      },
    },
    typography: {
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontFamily: ['"General Sans"', 'sans-serif', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', 'Helvetica', 'Arial'].join(','),
      body1: {
        fontSize: '16',
      },
      body2: {
        fontSize: 18,
        fontWeight: 500,
      },
      caption: {
        fontSize: '14px',
      },
      h1: {
        fontSize: '20px',
        fontWeight: 500,
        '@media (min-width:1710)': {
          fontSize: '15px',
        },
      },
      h2: {
        fontSize: '40px',
      },
      h3: {
        fontSize: '32px',
      },
      h4: {
        fontSize: '24px',
      },
      h5: {
        fontSize: '16px',
      },
      h6: {
        fontSize: '8px',
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderRadius: '12px',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            minWidth: '520px',
            [theme.breakpoints.down('md')]: {
              minWidth: '320px !important',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              border: '0.5px solid #B2B3B7',
              fontFamily: '"General Sans", sans-serif',
              background: '#fff',
              '& input:-webkit-autofill': {
                '-webkit-box-shadow': '0 0 0 30px white inset !important',
                '-webkit-text-fill-color': 'black !important',
              },
              [theme.breakpoints.down('md')]: {
                borderRadius: '8px',
                '& input': {
                  padding: '10px 14px',
                },
              },
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                display: 'none',
              },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              border: '0.5px solid #B2B3B7',
              fontFamily: '"General Sans", sans-serif',
              [theme.breakpoints.down('md')]: {
                borderRadius: '8px',
              },
              '& .MuiSelect-select': {
                height: '15px',
              },
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontFamily: '"General Sans", sans-serif',
            minWidth: '120px',
            fontSize: '16px',
            height: '48px',
            fontWeight: '500',
            borderRadius: '8px',
            textWrap: 'nowrap',
            boxShadow: 'none',
            '@media (max-width: 1440px)': {
              minWidth: '100px',
              fontSize: '12px',
              height: '38px',
              fontWeight: '400',
            },
            [theme.breakpoints.down('md')]: {
              height: '44px',
            },
          },
        },
        variants: [
          {
            props: { variant: 'contained' },
            style: {
              background: '#604797',
              color: '#fff',
              padding: '12px',
              ':hover': {
                background: '#4B3776',
              },
            },
          },
          {
            props: { variant: 'outlined' },
            style: {
              background: 'white',
              color: '#333333',
              padding: '12px',
              border: '1px solid #D1C8E3',
            },
          },
          {
            props: { variant: 'outlinedII' },
            style: {
              background: 'white',
              color: '#604797',
              padding: '8px 12px',
              fontWeight: '600',
              fontSize: '16px',
              border: '1px solid #604797',
              ':hover': {
                background: '#D1C8E3',
              },
            },
          },
          {
            props: { variant: 'containedII' },
            style: {
              background: '#F0EDF6',
              color: '#604797',
              padding: '12px',
            },
          },
          {
            props: { variant: 'containedIII' },
            style: {
              background: '#FBEAEA',
              color: '#962121',
              padding: '12px',
            },
          },
          {
            props: { variant: 'containedIV' },
            style: {
              background: '#EBF4EC',
              color: '#28652B',
              padding: '12px',
            },
          },
          {
            props: { variant: 'text' },
            style: {
              color: '#6A4EA6',
              fontSize: '16px',
              fontWeight: '600',
              minWidth: '0px',
              padding: '4px',
              [`${theme.breakpoints.down('md')}`]: {
                textDecoration: 'underline',
                fontSize: '14px',
              },
            },
          },
        ],
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: '"General Sans", sans-serif',
          },
        },
        variants: [
          {
            props: { variant: 'extralargebold' },
            style: {
              fontSize: '24px',
              fontWeight: '600',
            },
          },
          {
            props: { variant: 'extralargemedium' },
            style: {
              fontSize: '24px',
              fontWeight: '500',
            },
          },
          {
            props: { variant: 'extralargethin' },
            style: {
              fontSize: '24px',
              fontWeight: '400',
            },
          },
          {
            props: { variant: 'largebold' },
            style: {
              fontSize: '20px',
              fontWeight: '600',
            },
          },
          {
            props: { variant: 'largemedium' },
            style: {
              fontSize: '20px',
              fontWeight: '500',
            },
          },
          {
            props: { variant: 'largethin' },
            style: {
              fontSize: '20px',
              fontWeight: '400',
            },
          },
          {
            props: { variant: 'mediumbold' },
            style: {
              fontSize: '16px',
              fontWeight: '600',
            },
          },
          {
            props: { variant: 'medium' },
            style: {
              fontSize: '16px',
              fontWeight: '500',
            },
          },
          {
            props: { variant: 'mediumthin' },
            style: {
              fontSize: '16px',
              fontWeight: '400',
            },
          },
          {
            props: { variant: 'smallbold' },
            style: {
              fontSize: '14px',
              fontWeight: '600',
            },
          },
          {
            props: { variant: 'smallmedium' },
            style: {
              fontSize: '14px',
              fontWeight: '500',
            },
          },
          {
            props: { variant: 'smallthin' },
            style: {
              fontSize: '14px',
              fontWeight: '400',
            },
          },
          {
            props: { variant: 'extrasmallbold' },
            style: {
              fontSize: '12px',
              fontWeight: '600',
            },
          },
          {
            props: { variant: 'extrasmallmedium' },
            style: {
              fontSize: '12px',
              fontWeight: '500',
            },
          },
          {
            props: { variant: 'extrasmallthin' },
            style: {
              fontSize: '12px',
              fontWeight: '400',
            },
          },
        ],
      },
    },
  });

  return <ThemeProvider theme={responsive}>{children}</ThemeProvider>;
};

export default GlobalThemeProvider;
